import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import Text from '../../../../components/Text';
import { styles } from '../../../../styles/themes/style';
import { AppRoutes } from '../../../../interfaces/store/appStore';
import { ICampaign } from '../../../../interfaces/models/campaign';
import { IProxyAccountDetails } from '../../../../services/apis/campaigns/useCampaignsRequest';

interface Props {
  campaign: ICampaign;
  proxyAccounts: Record<string, IProxyAccountDetails>;
}

// Statuses that mean the account can still serve a campaign — everything else
// (credentials/stopped/disconnected/not_found) needs the user to reconnect.
// 'error' is a transient lookup failure, not a real disconnect, so it's ignored.
const HEALTHY = new Set(['connected', 'pending', 'error']);

/**
 * Warns when a proxy campaign has a LinkedIn account that disconnected on the
 * provider side. Leads already bound to that account can't be actioned: their
 * steps retry with backoff and eventually fail until the account is reconnected.
 */
const CampaignAccountReconnectBanner: React.FC<Props> = ({ campaign, proxyAccounts }) => {
  if ((campaign.executionMethod ?? 'extension') !== 'proxy') return null;

  const broken = Object.values(proxyAccounts).filter(a => !HEALTHY.has(a.status));
  if (!broken.length) return null;

  const names = broken.map(a => a.name || `Account ${a.accountId.slice(0, 8)}…`).join(', ');
  const editTo = AppRoutes.CAMPAIGN_EDIT.replace(':id', campaign._id);

  return (
    <Banner>
      <Text $paragraph2>
        <strong>{broken.length === 1 ? 'LinkedIn account disconnected' : `${broken.length} LinkedIn accounts disconnected`}</strong>
        {' — '}{names}
        {'. Leads assigned to '}{broken.length === 1 ? 'it' : 'them'}{' are paused and will fail after repeated retries until you reconnect.'}
        {' '}
        <EditLink to={editTo}>Reconnect now</EditLink>
      </Text>
    </Banner>
  );
};

const Banner = styled.div`
  padding: 12px 16px;
  margin-bottom: 12px;
  border-radius: 8px;
  border: 1px solid ${styles.colors.red300};
  background: ${styles.colors.red200};
`;

const EditLink = styled(Link)`
  color: ${styles.colors.primary600};
  font-weight: 600;
  text-decoration: underline;
`;

export default CampaignAccountReconnectBanner;
